import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5020/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error.response?.data || error.message);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  googleAuth: (data) => api.post('/auth/google', data),
  sendWhatsAppOTP: (data) => api.post('/auth/whatsapp/send-otp', data),
  verifyWhatsAppOTP: (data) => api.post('/auth/whatsapp/verify-otp', data),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  changePassword: (data) => api.put('/auth/change-password', data),
  addAddress: (data) => api.post('/auth/addresses', data),
  updateAddress: (id, data) => api.put(`/auth/addresses/${id}`, data),
  deleteAddress: (id) => api.delete(`/auth/addresses/${id}`)
};

// Product APIs
export const productAPI = {
  getProducts: (params) => api.get('/products', { params }),
  getProduct: (id) => api.get(`/products/${id}`),
  getProductBySlug: (slug) => api.get(`/products/slug/${slug}`),
  getFeatured: () => api.get('/products/featured'),
  getCategories: () => api.get('/products/categories'),
  getBrands: () => api.get('/products/brands'),
  addReview: (id, data) => api.post(`/products/${id}/reviews`, data)
};

// Order APIs
export const orderAPI = {
  createRazorpayOrder: (data) => api.post('/orders/razorpay/create', data),
  verifyRazorpayPayment: (data) => api.post('/orders/razorpay/verify', data),
  createOrder: (data) => api.post('/orders', data),
  getOrders: (params) => api.get('/orders', { params }),
  getOrder: (id) => api.get(`/orders/${id}`),
  updateOrder: (id, data) => api.put(`/orders/${id}`, data),
  cancelOrder: (id, data) => api.put(`/orders/${id}/cancel`, data),
  getInvoice: (id) => api.get(`/orders/${id}/invoice`),
  downloadInvoice: (id) => {
    window.open(`${API_URL}/orders/${id}/invoice/download`, '_blank');
  }
};

// Payment APIs
export const paymentAPI = {
  createPayment: (data) => api.post('/payments/create', data),
  getPayment: (id) => api.get(`/payments/${id}`),
  verifyRazorpay: (data) => api.post('/payments/verify-razorpay', data)
};

// Category APIs
export const categoryAPI = {
  getCategories: () => api.get('/categories'),
  getCategory: (id) => api.get(`/categories/${id}`),
  createCategory: (data) => api.post('/categories', data),
  updateCategory: (id, data) => api.put(`/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/categories/${id}`),
  getCategoryTree: () => api.get('/categories/tree')
};

// Coupon APIs
export const couponAPI = {
  getCoupons: () => api.get('/coupons'),
  getCoupon: (id) => api.get(`/coupons/${id}`),
  createCoupon: (data) => api.post('/coupons', data),
  updateCoupon: (id, data) => api.put(`/coupons/${id}`, data),
  deleteCoupon: (id) => api.delete(`/coupons/${id}`),
  validateCoupon: (data) => api.post('/coupons/validate', data),
  applyCoupon: (data) => api.post('/coupons/apply', data)
};

// Banner APIs
export const bannerAPI = {
  getBanners: () => api.get('/admin/banners'),
  getBanner: (id) => api.get(`/admin/banners/${id}`),
  createBanner: (data) => api.post('/admin/banners', data),
  updateBanner: (id, data) => api.put(`/admin/banners/${id}`, data),
  deleteBanner: (id) => api.delete(`/admin/banners/${id}`),
  uploadBannerImage: (id, formData) => api.post(`/admin/banners/${id}/image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
};

// Review APIs
export const reviewAPI = {
  getProductReviews: (productId) => api.get(`/reviews/product/${productId}`),
  createReview: (data) => api.post('/reviews', data),
  updateReview: (id, data) => api.put(`/reviews/${id}`, data),
  deleteReview: (id) => api.delete(`/reviews/${id}`),
  markHelpful: (id) => api.put(`/reviews/${id}/helpful`),
  getAllReviews: () => api.get('/reviews'),
  approveReview: (id, data) => api.put(`/reviews/${id}/approve`, data)
};

// Admin APIs
export const adminAPI = {
  // Products
  createProduct: (data) => api.post('/admin/products', data),
  updateProduct: (id, data) => api.put(`/admin/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/admin/products/${id}`),
  uploadImages: (id, formData) => api.post(`/admin/products/${id}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteImage: (productId, imageId) => api.delete(`/admin/products/${productId}/images/${imageId}`),
  getLowStock: () => api.get('/admin/products/low-stock'),

  // Orders
  getAllOrders: (params) => api.get('/admin/orders', { params }),
  getOrder: (id) => api.get(`/admin/orders/${id}`),
  updateOrder: (id, data) => api.put(`/admin/orders/${id}`, data),
  updateOrderStatus: (id, data) => api.put(`/admin/orders/${id}/status`, data),
  cancelOrder: (id) => api.put(`/admin/orders/${id}/cancel`),

  // Shipments
  createShipment: (data) => api.post('/admin/shipments', data),
  schedulePickup: (data) => api.post('/admin/shipments/pickup', data),
  getAvailableCouriers: (id) => api.get(`/admin/shipments/${id}/couriers`),
  assignCourier: (id, data) => api.post(`/admin/shipments/${id}/assign-courier`, data),
  generateAWB: (id, data) => api.post(`/admin/shipments/${id}/generate-awb`, data),
  generateLabel: (id) => api.get(`/admin/shipments/${id}/label`),
  generateInvoice: (id) => api.get(`/admin/shipments/${id}/invoice`),
  requestPickup: (id) => api.post(`/admin/shipments/${id}/pickup`),
  cancelShipment: (id) => api.post(`/admin/shipments/${id}/cancel`),
  initiateReturn: (id) => api.post(`/admin/shipments/${id}/return`),
  syncShipment: (id) => api.post(`/admin/shipments/${id}/sync`),
  getWalletBalance: () => api.get('/admin/shipments/wallet-balance'),

  // Users
  getAllUsers: (params) => api.get('/admin/users', { params }),
  getUser: (id) => api.get(`/admin/users/${id}`),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  getUserOrders: (id, params) => api.get(`/admin/users/${id}/orders`, { params }),
  toggleUserStatus: (id) => api.patch(`/admin/users/${id}/toggle-status`),

  // Banners
  getAllBanners: () => api.get('/admin/banners'),
  getBanner: (id) => api.get(`/admin/banners/${id}`),
  createBanner: (data) => api.post('/admin/banners', data),
  updateBanner: (id, data) => api.put(`/admin/banners/${id}`, data),
  deleteBanner: (id) => api.delete(`/admin/banners/${id}`),
  uploadBannerImage: (id, formData) => api.post(`/admin/banners/${id}/image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // Settings
  getAllSettings: (params) => api.get('/admin/settings', { params }),
  getSetting: (key) => api.get(`/admin/settings/${key}`),
  updateSetting: (key, data) => api.put(`/admin/settings/${key}`, data),
  bulkUpdateSettings: (data) => api.post('/admin/settings/bulk', data),
  deleteSetting: (key) => api.delete(`/admin/settings/${key}`),
  initializeSettings: () => api.post('/admin/settings/initialize'),

  // Reports
  getDashboardStats: () => api.get('/admin/reports/dashboard'),
  getSalesReport: (params) => api.get('/admin/reports/sales', { params }),
  getProductReport: () => api.get('/admin/reports/products'),
  getCustomerReport: () => api.get('/admin/reports/customers')
};

// Public APIs
export const publicAPI = {
  getBanners: () => api.get('/banners'),
  getSettings: () => api.get('/settings')
};

// Payments
export const refund = (paymentId, data) => api.post(`/payments/${paymentId}/refund`, data)

export default api;
